import getAuthenticatedApiClient from 'app/constants/fileProcessingService';

/**
 * Centralized API handler for file processing service operations
 * Uses the same authentication mechanism as portal-common-lib services
 */
class FileProcessingApiHandler {
  /**
   * Get the authenticated API client instance
   */
  private static getApiClient() {
    return getAuthenticatedApiClient();
  }

  /**
   * Debug logging helper for API responses
   */
  private static logApiResponse(url: string, response: unknown, error?: unknown) {
    // eslint-disable-next-line no-console
    console.group(`📤 FPS API Response: ${url}`);
    if (error) {
      // Log the full error response for debugging
      if ((error as { response?: { data?: unknown } }).response?.data) {
        // eslint-disable-next-line no-console
        console.error(
          '🔍 Full Error Response:',
          JSON.stringify((error as { response?: { data?: unknown } }).response?.data, null, 2)
        );
      }

      // Log the request that was sent
      if ((error as { config?: { data?: unknown } }).config?.data) {
        // eslint-disable-next-line no-console
        console.error(
          '📤 Request Data Sent:',
          JSON.stringify((error as { config?: { data?: unknown } }).config?.data, null, 2)
        );
      }
    }
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  /**
   * Get file by ID with authentication
   */
  static async getFileById(fileId: string) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/${fileId}`;

    try {
      const response = await apiClient.get(url);
      this.logApiResponse(url, response.data);
      return response.data;
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }

  /**
   * Get all files with authentication
   */
  static async getAllFiles(
    companyId: string,
    fileType = 'APPROVED_EARNINGS_TIMESHEET',
    page = 0,
    size = 1000
  ) {
    const apiClient = this.getApiClient();
    const url = '/fps/files';
    const params = {
      type: fileType,
      companyId,
      page,
      size,
    };

    try {
      const response = await apiClient.get(url, { params });
      this.logApiResponse(url, response.data);
      return response.data;
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }

  /**
   * Get upload pre-signed URL with authentication
   */
  static async getUploadPreSignedUrl(
    fileName: string,
    companyId?: string,
    uploadType = 'APPROVED_EARNINGS_TIMESHEET',
    payCalendarId?: string
  ) {
    const apiClient = this.getApiClient();
    const url = '/fps/files/upload';
    // companyId is omitted for ASLAN_ADMIN default recognition card uploads (no company scope).
    const params = {
      fileName,
      uploadType,
      ...(companyId !== undefined ? { companyId } : {}),
      ...(payCalendarId !== undefined ? { payCalendarId } : {}),
    };

    try {
      const response = await apiClient.get(url, { params });
      this.logApiResponse(url, response.data);
      return response.data;
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }

  /**
   * Request file analysis with authentication
   */
  static async requestFileAnalysis(fileUploadId: string) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/upload/${fileUploadId}/analyse`;

    try {
      // fps only matches the body-less /analyse overload when Content-Type is explicitly
      // form-urlencoded. Axios strips the Content-Type header whenever data is undefined/null,
      // so an empty string body is sent to force it to keep the header.
      const response = await apiClient.post(url, '', {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      this.logApiResponse(url, response.data);
      return response.data;
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }

  /**
   * Confirm file upload with authentication
   */
  static async confirmFileUpload(fileUploadId: string, properties?: Record<string, unknown>) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/upload/${fileUploadId}/confirm`;

    try {
      // consumes = application/json server-side; an empty object body makes axios set that
      // Content-Type automatically (axios strips the header entirely when data is undefined).
      await apiClient.post(url, { properties: properties ?? {} });
      this.logApiResponse(url, 'Success - no response data');
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }

  /**
   * Cancel file upload with authentication
   */
  static async cancelFileUpload(
    fileUploadId: string,
    cancellationRequest: Record<string, unknown>
  ) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/upload/${fileUploadId}/cancel`;

    try {
      const response = await apiClient.post(url, cancellationRequest);
      this.logApiResponse(url, response.data);
      return response.data;
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }

  /**
   * Verify a file uploaded directly to storage with authentication.
   *
   * Used by the direct presign → PUT → verify flow (e.g. savings preset images). On success the
   * upload transitions to SUCCEEDED; on failure the storage object is removed and it is CANCELLED.
   */
  static async verifyUpload(fileUploadId: string) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/upload/${fileUploadId}/verify`;

    try {
      const response = await apiClient.post(url);
      this.logApiResponse(url, response.data);
      return response.data;
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }

  /**
   * Delete a file upload and its stored object with authentication.
   *
   * Only supported by the backend for upload types with no processing lifecycle (currently
   * savings preset images); other types are rejected.
   */
  static async deleteFile(fileId: string) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/${fileId}`;

    try {
      await apiClient.delete(url);
      this.logApiResponse(url, 'Success - no response data');
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }

  /**
   * Get download URL for a file with authentication
   */
  static async getFileDownloadUrl(fileId: string, fileName: string) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/${fileId}/download`;
    const params = {
      fileName,
    };

    try {
      const response = await apiClient.get(url, { params });
      this.logApiResponse(url, response.data);
      return response.data;
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }

  /**
   * Get download URL for a savings pot image, optionally a stored variant such as its thumbnail
   */
  static async getSavingsImageUrl(fileId: string, variant?: 'THUMBNAIL') {
    const apiClient = this.getApiClient();
    const url = `/fps/files/${fileId}/download`;

    try {
      const response = await apiClient.get(url, { params: { variant } });
      this.logApiResponse(url, response.data);
      return response.data;
    } catch (error) {
      this.logApiResponse(url, null, error);
      throw error;
    }
  }
}

export default FileProcessingApiHandler;
